import React, { useState, useEffect } from 'react';
import { Calendar, Link, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';
import { useToast } from '../hooks/use-toast';

interface GoogleCalendarIntegrationProps {
  onIntegrationChange?: (isConnected: boolean) => void;
}

const GoogleCalendarIntegration: React.FC<GoogleCalendarIntegrationProps> = ({
  onIntegrationChange
}) => {
  const { toast } = useToast();
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);

  useEffect(() => {
    checkConnectionStatus();
  }, []);

  const checkConnectionStatus = () => {
    const storedAccessToken = localStorage.getItem('google_calendar_access_token');
    const storedRefreshToken = localStorage.getItem('google_calendar_refresh_token');
    
    if (storedAccessToken && storedRefreshToken) {
      setAccessToken(storedAccessToken);
      setRefreshToken(storedRefreshToken);
      setIsConnected(true);
      onIntegrationChange?.(true);
    }
  };

  const getGoogleClientId = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('get-google-client-id');
      if (error) throw error;
      return data.clientId;
    } catch (error) {
      console.error('Erro ao obter Google Client ID:', error);
      throw error;
    }
  };

  const initiateGoogleAuth = async () => {
    setIsConnecting(true);
    
    try {
      const clientId = await getGoogleClientId();
      
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` + 
        `client_id=${encodeURIComponent(clientId)}&` +
        `redirect_uri=${encodeURIComponent(window.location.origin + '/google-calendar-callback')}&` +
        `response_type=code&` +
        `scope=${encodeURIComponent('https://www.googleapis.com/auth/calendar')}&` +
        `access_type=offline&` +
        `prompt=consent`;

      // Abrir popup para autenticação
      const popup = window.open(authUrl, 'google-auth', 'width=500,height=600');
      
      // Escutar mensagens do popup
      const messageListener = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        
        if (event.data.type === 'GOOGLE_AUTH_SUCCESS') {
          handleAuthSuccess(event.data.code);
          popup?.close();
          window.removeEventListener('message', messageListener);
        } else if (event.data.type === 'GOOGLE_AUTH_ERROR') {
          handleAuthError(event.data.error);
          popup?.close();
          window.removeEventListener('message', messageListener);
        }
      };

      window.addEventListener('message', messageListener);

      // Verificar se popup foi fechado manualmente
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          setIsConnecting(false);
          window.removeEventListener('message', messageListener);
        }
      }, 1000);
    } catch (error) {
      handleAuthError(error instanceof Error ? error.message : 'Erro ao iniciar autenticação');
    }
  };

  const handleAuthSuccess = async (code: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('google-calendar-sync', {
        body: {
          action: 'oauth_callback',
          code
        }
      });

      if (error) throw error;

      // Armazenar tokens
      localStorage.setItem('google_calendar_access_token', data.access_token);
      if (data.refresh_token) {
        localStorage.setItem('google_calendar_refresh_token', data.refresh_token);
      }

      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);
      setIsConnected(true);
      setIsConnecting(false);
      
      onIntegrationChange?.(true);

      toast({
        title: "Sucesso!",
        description: "Google Calendar conectado com sucesso.",
      });
    } catch (error) {
      console.error('Erro na autenticação:', error);
      handleAuthError(error instanceof Error ? error.message : 'Erro desconhecido');
    }
  };

  const handleAuthError = (error: string) => {
    setIsConnecting(false);
    toast({
      title: "Erro na Autenticação",
      description: `Falha ao conectar com Google Calendar: ${error}`,
      variant: "destructive",
    });
  };

  const disconnect = () => {
    localStorage.removeItem('google_calendar_access_token');
    localStorage.removeItem('google_calendar_refresh_token');
    setAccessToken(null);
    setRefreshToken(null);
    setIsConnected(false);
    onIntegrationChange?.(false);

    toast({
      title: "Desconectado",
      description: "Google Calendar desconectado com sucesso.",
    });
  };

  const refreshAccessToken = async () => {
    if (!refreshToken) return;

    try {
      const { data, error } = await supabase.functions.invoke('google-calendar-sync', {
        body: {
          action: 'refresh_token',
          refreshToken
        }
      });

      if (error) throw error;

      localStorage.setItem('google_calendar_access_token', data.access_token);
      setAccessToken(data.access_token);

      toast({
        title: "Token Atualizado",
        description: "Acesso ao Google Calendar renovado.",
      });
    } catch (error) {
      console.error('Erro ao renovar token:', error);
      toast({
        title: "Erro",
        description: "Falha ao renovar acesso. Tente reconectar.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="bg-card rounded-lg border p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Calendar className="h-6 w-6 text-primary" />
          <div>
            <h3 className="text-lg font-semibold">Integração Google Calendar</h3>
            <p className="text-sm text-muted-foreground">
              Sincronize entrevistas com sua agenda do Google
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {isConnected ? (
            <CheckCircle className="h-5 w-5 text-green-500" />
          ) : (
            <XCircle className="h-5 w-5 text-gray-400" />
          )}
          <span className="text-sm font-medium">
            {isConnected ? 'Conectado' : 'Desconectado'}
          </span>
        </div>
      </div>

      <div className="space-y-4">
        {!isConnected ? (
          <button
            onClick={initiateGoogleAuth}
            disabled={isConnecting}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isConnecting ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Conectando...
              </>
            ) : (
              <>
                <Link className="h-4 w-4" />
                Conectar Google Calendar
              </>
            )}
          </button>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              ✅ Suas entrevistas serão automaticamente criadas no Google Calendar
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={refreshAccessToken}
                className="flex-1 bg-secondary text-secondary-foreground hover:bg-secondary/80 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Renovar Acesso
              </button>
              
              <button
                onClick={disconnect}
                className="flex-1 bg-destructive text-destructive-foreground hover:bg-destructive/90 px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Desconectar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleCalendarIntegration;